import { Check, Target, TrendingDown } from 'lucide-react'

/**
 * Side bets configuration step shared between RoundSetupWizard and SessionSetupWizard.
 *
 * Props:
 *   gameTypeId, course,
 *   ctpEnabled, setCtpEnabled, ctpAmount, setCtpAmount,
 *   roundLowNetEnabled, setRoundLowNetEnabled, roundLowNetAmount, setRoundLowNetAmount,
 *   roundSkinsEnabled, setRoundSkinsEnabled, roundSkinsAmount, setRoundSkinsAmount,
 *   roundSkinsCarryOver, setRoundSkinsCarryOver,
 *   roundSkinsGrossTiebreaker, setRoundSkinsGrossTiebreaker,
 *   roundSkinsFullHandicap, setRoundSkinsFullHandicap,
 *   lowNetLabel — "round" or "session", used in description text
 *   headerNote — optional note below the "Side Games" heading
 *   children — optional extra content rendered after the skins toggle (e.g. match-level side bets)
 */
export default function StepSideBets({
  gameTypeId, course,
  ctpEnabled, setCtpEnabled, ctpAmount, setCtpAmount,
  roundLowNetEnabled, setRoundLowNetEnabled, roundLowNetAmount, setRoundLowNetAmount,
  roundSkinsEnabled, setRoundSkinsEnabled, roundSkinsAmount, setRoundSkinsAmount,
  roundSkinsCarryOver, setRoundSkinsCarryOver,
  roundSkinsGrossTiebreaker, setRoundSkinsGrossTiebreaker,
  roundSkinsFullHandicap, setRoundSkinsFullHandicap,
  lowNetLabel = 'round',
  headerNote,
  children,
}) {
  const par3Count = course ? course.par.filter((p) => p === 3).length : 0
  const isScramble = gameTypeId === 'scramble'
  const lowNetDisabled = gameTypeId === 'net_stroke_play' || isScramble
  const skinsDisabled = isScramble

  return (
    <div>
      <div className="form-section">
        <h3>Side Games</h3>
        {headerNote && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', margin: '0 0 0.5rem', lineHeight: 1.4 }}>
            {headerNote}
          </p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* CTP toggle */}
          <div
            onClick={() => setCtpEnabled(!ctpEnabled)}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem',
              padding: '0.75rem',
              backgroundColor: ctpEnabled ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
              borderRadius: '0.5rem',
              border: `1px solid ${ctpEnabled ? 'var(--accent-primary)' : 'var(--border-color)'}`,
              cursor: 'pointer',
            }}
          >
            <div style={{
              width: '20px', height: '20px', borderRadius: '0.25rem',
              border: `2px solid ${ctpEnabled ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
              backgroundColor: ctpEnabled ? 'var(--accent-primary)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {ctpEnabled && <Check size={14} style={{ color: '#fff' }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Target size={14} style={{ color: ctpEnabled ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
                <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Closest to Pin</span>
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                Per player per par 3 hole
              </div>
            </div>
            {ctpEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '600' }}>$</span>
                <input
                  type="number"
                  min="1"
                  value={ctpAmount}
                  onChange={(e) => setCtpAmount(e.target.value)}
                  style={{
                    width: '52px', height: '30px', textAlign: 'center', borderRadius: '0.375rem',
                    fontSize: '0.8125rem', fontWeight: '600', backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)', color: 'var(--text-primary)',
                  }}
                />
              </div>
            )}
            {ctpEnabled && par3Count > 0 && (
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem', width: '100%', paddingLeft: '1.75rem' }}>
                {par3Count} par 3s x ${Number(ctpAmount) || 0} = <span style={{ color: 'var(--accent-primary)', fontWeight: '600' }}>${par3Count * (Number(ctpAmount) || 0)}/player</span> total CTP
              </div>
            )}
          </div>

          {/* Round Lowest Net toggle */}
          <div
            onClick={() => !lowNetDisabled && setRoundLowNetEnabled(!roundLowNetEnabled)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem',
              backgroundColor: roundLowNetEnabled && !lowNetDisabled ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
              borderRadius: '0.5rem',
              border: `1px solid ${roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'var(--border-color)'}`,
              cursor: lowNetDisabled ? 'not-allowed' : 'pointer',
              opacity: lowNetDisabled ? 0.5 : 1,
            }}
          >
            <div style={{
              width: '20px', height: '20px', borderRadius: '0.25rem',
              border: `2px solid ${roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
              backgroundColor: roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {roundLowNetEnabled && !lowNetDisabled && <Check size={14} style={{ color: '#fff' }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <TrendingDown size={14} style={{ color: roundLowNetEnabled && !lowNetDisabled ? 'var(--accent-primary)' : 'var(--text-muted)' }} />
                <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Lowest Net</span>
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                {lowNetDisabled ? (isScramble ? 'Not available for Scramble — no individual scores' : 'Not available — Net Stroke Play is effectively the same game') : `Low net score for this ${lowNetLabel} wins the pot`}
              </div>
            </div>
            {roundLowNetEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '600' }}>$</span>
                <input
                  type="number"
                  min="1"
                  value={roundLowNetAmount}
                  onChange={(e) => setRoundLowNetAmount(e.target.value)}
                  style={{
                    width: '52px', height: '30px', textAlign: 'center', borderRadius: '0.375rem',
                    fontSize: '0.8125rem', fontWeight: '600', backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)', color: 'var(--text-primary)',
                  }}
                />
              </div>
            )}
          </div>

          {/* Skins toggle */}
          <div
            onClick={() => !skinsDisabled && setRoundSkinsEnabled(!roundSkinsEnabled)}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '0.75rem',
              backgroundColor: roundSkinsEnabled && !skinsDisabled ? 'rgba(var(--accent-primary-rgb, 14, 164, 122), 0.06)' : 'var(--bg-tertiary)',
              borderRadius: '0.5rem',
              border: `1px solid ${roundSkinsEnabled && !skinsDisabled ? 'var(--accent-primary)' : 'var(--border-color)'}`,
              cursor: skinsDisabled ? 'not-allowed' : 'pointer',
              opacity: skinsDisabled ? 0.5 : 1,
            }}
          >
            <div style={{
              width: '20px', height: '20px', borderRadius: '0.25rem',
              border: `2px solid ${roundSkinsEnabled && !skinsDisabled ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
              backgroundColor: roundSkinsEnabled && !skinsDisabled ? 'var(--accent-primary)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {roundSkinsEnabled && !skinsDisabled && <Check size={14} style={{ color: '#fff' }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <span style={{ color: roundSkinsEnabled && !skinsDisabled ? 'var(--accent-primary)' : 'var(--text-muted)', fontWeight: '700', fontSize: '14px' }}>$</span>
                <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>Skins</span>
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                {skinsDisabled ? 'Not available for Scramble — no individual scores' : 'Low net per hole wins a skin. Pot split by skins won'}
              </div>
            </div>
            {roundSkinsEnabled && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }} onClick={(e) => e.stopPropagation()}>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', fontWeight: '600' }}>$</span>
                <input
                  type="number"
                  min="1"
                  value={roundSkinsAmount}
                  onChange={(e) => setRoundSkinsAmount(e.target.value)}
                  style={{
                    width: '52px', height: '30px', textAlign: 'center', borderRadius: '0.375rem',
                    fontSize: '0.8125rem', fontWeight: '600', backgroundColor: 'var(--bg-secondary)',
                    border: '1px solid var(--border-color)', color: 'var(--text-primary)',
                  }}
                />
              </div>
            )}
            {roundSkinsEnabled && (
              <div
                onClick={(e) => { e.stopPropagation(); setRoundSkinsCarryOver(!roundSkinsCarryOver) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  paddingTop: '0.5rem',
                  paddingLeft: '2rem',
                  marginTop: '0.25rem',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '16px', height: '16px', borderRadius: '0.1875rem',
                  border: `2px solid ${roundSkinsCarryOver ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: roundSkinsCarryOver ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {roundSkinsCarryOver && <Check size={10} style={{ color: '#fff' }} />}
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Allow Carry-Overs</span>
                  <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Tied holes roll skins to the next hole</div>
                </div>
              </div>
            )}
            {roundSkinsEnabled && (
              <div
                onClick={(e) => { e.stopPropagation(); setRoundSkinsGrossTiebreaker(!roundSkinsGrossTiebreaker) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  paddingTop: '0.5rem',
                  paddingLeft: '2rem',
                  marginTop: '0.25rem',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '16px', height: '16px', borderRadius: '0.1875rem',
                  border: `2px solid ${roundSkinsGrossTiebreaker ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: roundSkinsGrossTiebreaker ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {roundSkinsGrossTiebreaker && <Check size={10} style={{ color: '#fff' }} />}
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Low Gross Wins</span>
                  <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Tied net scores broken by lower gross</div>
                </div>
              </div>
            )}
            {roundSkinsEnabled && (
              <div
                onClick={(e) => { e.stopPropagation(); setRoundSkinsFullHandicap(!roundSkinsFullHandicap) }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  paddingTop: '0.5rem',
                  paddingLeft: '2rem',
                  marginTop: '0.25rem',
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '16px', height: '16px', borderRadius: '0.1875rem',
                  border: `2px solid ${roundSkinsFullHandicap ? 'var(--accent-primary)' : 'var(--text-muted)'}`,
                  backgroundColor: roundSkinsFullHandicap ? 'var(--accent-primary)' : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {roundSkinsFullHandicap && <Check size={10} style={{ color: '#fff' }} />}
                </div>
                <div>
                  <span style={{ fontSize: '0.75rem', fontWeight: '500', color: 'var(--text-secondary)' }}>Full Course Handicap</span>
                  <div style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Use 100% course handicap instead of game handicaps</div>
                </div>
              </div>
            )}
          </div>
        </div>

        {children}
      </div>
    </div>
  )
}
